import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Facebook, Instagram, Music2, Send, CheckCircle } from 'lucide-react';
import { contactInfo } from '../data/mockData';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    setFormData({ firstName: '', lastName: '', email: '', message: '' });
    
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  return (
    <main>
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[350px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1625246333195-78d9c38ad449?w=1600&auto=format&fit=crop&q=80"
            alt="Montana landscape"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#3B2F1E]/90 via-[#3B2F1E]/50 to-[#2D5016]/30" />
        </div>
        <div className="relative z-10 text-center px-4 max-w-3xl mx-auto">
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-white/80 text-lg">
            We're here to help with questions about our organic and non-GMO animal feed, pasture-grown products, delivery options in Montana, or anything else you need. We love hearing from farmers, homesteaders, and animal lovers.
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-24 bg-[#FAF6F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-[#2D5016]/10 text-[#2D5016] text-xs font-semibold uppercase tracking-wider mb-4">
                Get In Touch
              </span>
              <h2 className="font-serif text-4xl font-bold text-[#3B2F1E] mb-8">
                We'd Love to Hear From You
              </h2>

              <div className="space-y-6">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#3B2F1E] mb-1">Address</h3>
                    <p className="text-[#3B2F1E]/70">{contactInfo.address}</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#3B2F1E] mb-1">Phone</h3>
                    <a
                      href={`tel:${contactInfo.phone}`}
                      className="text-[#2D5016] hover:text-[#A0522D] transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#3B2F1E] mb-1">Email</h3>
                    <a
                      href={`mailto:${contactInfo.email}`}
                      className="text-[#2D5016] hover:text-[#A0522D] transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </div>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-[#2D5016] flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#3B2F1E] mb-1">Hours</h3>
                    <p className="text-[#3B2F1E]/70">{contactInfo.hours.weekdays}</p>
                    <p className="text-[#3B2F1E]/70">{contactInfo.hours.sunday}</p>
                  </div>
                </div>
              </div>

              {/* Social Icons */}
              <div className="mt-10">
                <h3 className="font-semibold text-[#3B2F1E] mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a
                    href={contactInfo.social.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#3B2F1E]/10 flex items-center justify-center hover:bg-[#2D5016] hover:text-white transition-all duration-300 text-[#3B2F1E]"
                  >
                    <Facebook className="w-5 h-5" />
                  </a>
                  <a
                    href={contactInfo.social.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#3B2F1E]/10 flex items-center justify-center hover:bg-[#2D5016] hover:text-white transition-all duration-300 text-[#3B2F1E]"
                  >
                    <Instagram className="w-5 h-5" />
                  </a>
                  <a
                    href={contactInfo.social.tiktok}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-[#3B2F1E]/10 flex items-center justify-center hover:bg-[#2D5016] hover:text-white transition-all duration-300 text-[#3B2F1E]"
                  >
                    <Music2 className="w-5 h-5" />
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-3xl shadow-xl p-8 md:p-10">
              <h3 className="font-serif text-2xl font-bold text-[#3B2F1E] mb-6">
                Send Us a Message
              </h3>

              {isSubmitted ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-16 h-16 rounded-full bg-[#2D5016]/10 flex items-center justify-center mb-4">
                    <CheckCircle className="w-8 h-8 text-[#2D5016]" />
                  </div>
                  <h4 className="font-serif text-xl font-bold text-[#3B2F1E] mb-2">
                    Message Sent!
                  </h4>
                  <p className="text-[#3B2F1E]/70">
                    Thank you for reaching out. We'll get back to you soon!
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName" className="text-[#3B2F1E]">
                        First Name
                      </Label>
                      <Input
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        placeholder="John"
                        required
                        className="bg-[#FAF6F0] border-[#3B2F1E]/20 focus:border-[#2D5016] focus:ring-[#2D5016]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName" className="text-[#3B2F1E]">
                        Last Name
                      </Label>
                      <Input
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        placeholder="Doe"
                        required
                        className="bg-[#FAF6F0] border-[#3B2F1E]/20 focus:border-[#2D5016] focus:ring-[#2D5016]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#3B2F1E]">
                      Email
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="bg-[#FAF6F0] border-[#3B2F1E]/20 focus:border-[#2D5016] focus:ring-[#2D5016]"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message" className="text-[#3B2F1E]">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="How can we help you?"
                      required
                      rows={5}
                      className="bg-[#FAF6F0] border-[#3B2F1E]/20 focus:border-[#2D5016] focus:ring-[#2D5016] resize-none"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#A0522D] hover:bg-[#8B4513] text-white font-semibold py-6 rounded-lg transition-colors flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-96 bg-[#2D5016]/10">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2688.5!2d-114.3!3d48.2!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDjCsDEyJzAwLjAiTiAxMTTCsDE4JzAwLjAiVw!5e0!3m2!1sen!2sus!4v1234567890!5m2!1sen!2sus"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="Good Grains Location"
          className="grayscale hover:grayscale-0 transition-all duration-500"
        />
      </section>
    </main>
  );
};

export default ContactPage;
